import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, Package, Zap, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { investorAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const InvestorPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [opportunities, setOpportunities] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvestment, setSelectedInvestment] = useState(null);

  useEffect(() => {
    if (user?.role === 'investor' && user.status === 'active') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [oppRes, portRes] = await Promise.all([
        investorAPI.getOpportunities(),
        investorAPI.getPortfolio()
      ]);
      setOpportunities(oppRes.data);
      setPortfolio(portRes.data);
    } catch (error) {
      console.error('Error fetching investor data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'investor') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <TrendingUp className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Investor Portal Access Required</h1>
          <p className="text-gray-600 mb-8">You need to register as an investor to access this portal.</p>
          <Button onClick={() => navigate('/role-selection')} className="bg-red-600 hover:bg-red-700">
            Register as Investor
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (user.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <Card className="max-w-2xl mx-auto p-8 text-center">
            <Clock className="h-24 w-24 text-orange-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Application Pending</h1>
            <p className="text-gray-600 mb-6">
              Your investor application is under review. You'll receive an email once approved.
            </p>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Investor Portal</h1>
          <p className="text-gray-600">Invest in products and designs, track your portfolio</p>
        </div>

        {/* Portfolio Stats */}
        {portfolio && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Invested</p>
                  <p className="text-3xl font-bold text-green-700">₹{portfolio.total_invested.toLocaleString()}</p>
                </div>
                <DollarSign className="h-12 w-12 text-green-600" />
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Active Investments</p>
                  <p className="text-3xl font-bold">{portfolio.active_investments}</p>
                </div>
                <Package className="h-12 w-12 text-green-600" />
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Expected Returns</p>
                  <p className="text-3xl font-bold text-green-600">
                    ₹{Math.round(portfolio.total_invested * 0.15).toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-12 w-12 text-green-600" />
              </div>
            </Card>
          </div>
        )}

        <Tabs defaultValue="opportunities" className="space-y-6">
          <TabsList>
            <TabsTrigger value="opportunities">Investment Opportunities</TabsTrigger>
            <TabsTrigger value="portfolio">My Portfolio</TabsTrigger>
          </TabsList>

          <TabsContent value="opportunities">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Mold Investments */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <Zap className="h-6 w-6 text-yellow-600 mr-2" />
                  Mold Investments (20% Returns)
                </h2>
                <div className="space-y-4">
                  {opportunities?.mold_investments?.length === 0 ? (
                    <Card className="p-8 text-center">
                      <p className="text-gray-600">No mold investment opportunities available</p>
                    </Card>
                  ) : (
                    opportunities?.mold_investments?.map((design) => (
                      <Card key={design.id} className="p-6 hover:shadow-lg transition-shadow">
                        <h3 className="font-bold text-lg mb-2">{design.design_name}</h3>
                        <p className="text-gray-600 text-sm mb-4">{design.description}</p>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500">Est. Cost</p>
                            <p className="font-bold">₹{design.estimated_cost.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Expected Return</p>
                            <p className="font-bold text-green-600">20%</p>
                          </div>
                        </div>
                        <Button 
                          className="w-full bg-yellow-600 hover:bg-yellow-700"
                          onClick={() => setSelectedInvestment({ ...design, type: 'mold' })}
                        >
                          Invest in Mold
                        </Button>
                      </Card>
                    ))
                  )}
                </div>
              </div>

              {/* Inventory Investments */}
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <Package className="h-6 w-6 text-red-600 mr-2" />
                  Inventory Investments (10% Returns)
                </h2>
                <div className="space-y-4">
                  {opportunities?.inventory_investments?.length === 0 ? (
                    <Card className="p-8 text-center">
                      <p className="text-gray-600">No inventory investment opportunities available</p>
                    </Card>
                  ) : (
                    opportunities?.inventory_investments?.map((product) => (
                      <Card key={product.id} className="p-6 hover:shadow-lg transition-shadow">
                        <div className="flex gap-4">
                          <img src={product.image} alt={product.name} className="w-20 h-20 object-cover rounded" />
                          <div className="flex-1">
                            <h3 className="font-bold mb-1">{product.name}</h3>
                            <p className="text-xs text-gray-600 mb-3">{product.category}</p>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-xs text-gray-500">Price</p>
                                <p className="font-bold">₹{product.price}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Expected Return</p>
                                <p className="font-bold text-green-600">10%</p>
                              </div>
                            </div>
                            <Button 
                              className="w-full bg-red-600 hover:bg-red-700"
                              onClick={() => setSelectedInvestment({ ...product, type: 'inventory' })}
                              size="sm"
                            >
                              Invest in Inventory
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portfolio">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">My Investments</h2>
              {portfolio?.investments?.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No investments yet</p>
                  <Button onClick={() => document.querySelector('[value="opportunities"]').click()} className="bg-red-600 hover:bg-red-700">
                    Explore Opportunities
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {portfolio.investments.map((inv) => (
                    <div key={inv.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            inv.investment_type === 'mold' ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {inv.investment_type.charAt(0).toUpperCase() + inv.investment_type.slice(1)} Investment
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          inv.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {inv.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Amount</p>
                          <p className="font-bold">₹{inv.amount.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Units</p>
                          <p className="font-bold">{inv.units}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Expected Return</p>
                          <p className="font-bold text-green-600">{Math.round(inv.expected_return * 100 - 100)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date</p>
                          <p className="text-sm">{new Date(inv.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {selectedInvestment && (
        <InvestmentModal 
          investment={selectedInvestment} 
          onClose={() => setSelectedInvestment(null)}
          onSuccess={() => {
            fetchData();
            setSelectedInvestment(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
};

const InvestmentModal = ({ investment, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState('');
  const [units, setUnits] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        investment_type: investment.type,
        product_id: investment.type === 'inventory' ? investment.id : undefined,
        design_id: investment.type === 'mold' ? investment.id : undefined,
        amount: parseInt(amount),
        units: parseInt(units)
      };

      await investorAPI.invest(data);
      
      toast({
        title: "Investment Successful!",
        description: `You've invested ₹${amount} successfully.`,
      });
      
      onSuccess();
    } catch (error) {
      toast({
        title: "Investment Failed",
        description: error.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const expectedReturn = investment.type === 'mold' ? 1.20 : 1.10;
  const returnAmount = amount ? Math.round(parseInt(amount) * expectedReturn) : 0;
  const profit = returnAmount - (parseInt(amount) || 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full p-6">
        <h2 className="text-2xl font-bold mb-4">Invest in {investment.type === 'mold' ? 'Mold' : 'Inventory'}</h2>
        <p className="text-gray-600 mb-6">{investment.design_name || investment.name}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Investment Amount (₹) *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              placeholder="10000"
            />
          </div>

          <div>
            <Label>Number of Units *</Label>
            <Input
              type="number"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              required
              placeholder="100"
            />
          </div>

          {amount && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Investment:</span>
                <span className="font-bold">₹{parseInt(amount).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Expected Return:</span>
                <span className="font-bold text-green-600">₹{returnAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <span>Profit:</span>
                <span className="font-bold text-green-700">₹{profit.toLocaleString()} ({Math.round((expectedReturn - 1) * 100)}%)</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700">
              {submitting ? 'Processing...' : 'Confirm Investment'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default InvestorPortal;
